import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, generatePath, useLocation, useParams } from "react-router-dom";

import { Button, Status } from "@snapchat/snap-design-system";
import { clsx } from "clsx";
import { FormikProvider, useFormik } from "formik";
import { useIntl } from "react-intl";

import { myLensesActionType } from "../../analytics/event-creators/createActionEvent";
import { useBlizzardEvents } from "../../analytics/hooks/useBlizzardEvents";
import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { AbsoluteRoute, type RouteParams } from "../../core/routes";
import { useLensManagementRoutes } from "../../core/routes/hooks";
import { CofConfigId, SurfaceType, Visibility } from "../../gql-generated/types";
import { Card, CardTitle, PageHeader, PageSubHeader } from "../../layout/components";
import { PageContentSpinner } from "../../layout/components/PageContentSpinner";
import { LensPreview } from "../../lens-preview/components/LensPreview";
import { useLensPreviewState } from "../../lens-preview/hooks";
import { GAME_LENS_VIDEO_DURATION_REQUIREMENT } from "../../lens-preview/validation";
import { useToggleState } from "../../shared/hooks";
import { type LensPreviewMediaItem } from "../../shared/state";
import { TextLink } from "../../shared/styles";
import { LensSubmissionDisclaimer } from "../../submit/components/lens-details/shared";
import { InvalidPreviewBadge } from "../../submit/components/lens-details/shared/messages.generated";
import { useGameLensToggle } from "../../submit/components/lens-details/shared/useGameLensToggle";
import { validateExclusiveLens } from "../../submit/validation";
import { useSubmitWebLens } from "../hooks/useSubmitWebLens";
import { useWebLensBuildOutputMeasurement } from "../hooks/useWebLensBuildOutputMeasurement";
import { useWebLensFolderSelection } from "../hooks/useWebLensFolderSelection";
import { useWebLensPreviewValidation } from "../hooks/useWebLensPreviewValidation";
import { useWebLensVisibilityConfig } from "../hooks/useWebLensVisibilityConfig";
import { LOGO_MAX_BYTES, LOGO_MAX_DIMENSION_PX, type SubmissionCheck } from "../model";
import {
    formatMegabytes,
    isBuildOutputWithinBudget,
    isLogoAccepted,
    isPackageSizeAccepted,
    isZipPackage,
} from "../validation/preChecks";
import { getReadinessChecks } from "../validation/readinessChecks";
import { type WebLensSubmitFormValues, webLensSubmitFormSchema } from "../validation/webLensFormSchemas";

import { SubmissionChecklist } from "./SubmissionChecklist";
import styles from "./WebLensPage.module.css";
import { WebLensPublishModal } from "./WebLensPublishModal";
import { WebLensGameLensToggle } from "./fields/WebLensGameLensToggle";
import { WebLensIconControl } from "./fields/WebLensIconControl";
import { WebLensNameField } from "./fields/WebLensNameField";
import { WebLensPackageDropzone } from "./fields/WebLensPackageDropzone";
import {
    ChangeDestination,
    DetailsSectionTitle,
    DocumentationLink,
    LogoHint,
    PackageSectionTitle,
    PageSubtitle,
    PageTitle,
    RailTitle,
    RequirementPreviewGame,
    RequirementPreviewVideo,
    SaveAsDraftCta,
    SubmitCta,
    SubmitDestination,
    SubmittingCta,
    UploadLogoCta,
    messages,
} from "./messages.generated";

const SNAPCHAT_SURFACE = [SurfaceType.SurfaceTypeSnapchat] as const;
const WEB_LENS_DOCUMENTATION_URL = "https://developers.snap.com/web-lenses/home";

export const WebLensSubmitPage: FC = () => {
    const intl = useIntl();
    const location = useLocation();
    const { orgId = "" } = useParams<RouteParams[AbsoluteRoute.WEB_LENSES_SUBMIT]>();
    const { sendBlizzardEvent } = useBlizzardEvents();
    const { loading: folderSelectionLoading, selectedOrganization, selectedFolder } = useWebLensFolderSelection();
    const {
        visibilityConfig,
        loading: visibilityPermissionsLoading,
        error: visibilityPermissionsError,
    } = useWebLensVisibilityConfig({ lensFolderId: selectedFolder?.id ?? "" });
    const [file, setFile] = useState<File | null>(null);
    const buildOutput = useWebLensBuildOutputMeasurement(file);
    const [logo, setLogo] = useState<File | null>(null);
    const [isMarkedExclusive, setIsMarkedExclusive] = useState(false);
    const [lensCreatorPayoutEnrolled, setLensCreatorPayoutEnrolled] = useState(false);
    // Creator-declared game marking, the same panel and Blizzard events as the submission flow.
    const [isGameUserProvided, setIsGameUserProvided] = useState(false);
    const { handleGameLensToggle } = useGameLensToggle({ setIsGameUserProvided });

    // Same gate the lens-submission flow puts on its whole monetization block (PublishModal).
    const { configs } = useFeatureGates();
    const isMyLensesCreatorPayoutEnabled = configs[CofConfigId.MyLensesCreatorPayout].enabled;

    const [previewMediaItems, setPreviewMediaItems] = useState<LensPreviewMediaItem[]>([]);
    const { uploadFileLoading, ...lensPreviewProps } = useLensPreviewState({
        previewMediaItems,
        setPreviewMediaItems,
        videoDurationRequirement: GAME_LENS_VIDEO_DURATION_REQUIREMENT,
        videoOnly: true,
    });
    const { errorMessage: previewError, hasVideoPreview } = useWebLensPreviewValidation({
        previewMediaItems,
        previewValidationMessage: lensPreviewProps.validationMessage,
    });
    const visiblePreviewError = lensPreviewProps.isTouched ? previewError : null;
    const { checks, result, submitting, submit, reset } = useSubmitWebLens();
    const { goToLensPage } = useLensManagementRoutes();

    // The lens exists from here on, so its own page is the right place to be: that is where
    // conversion progress, an archive rejection and the publish CTA live. Keyed on the id rather
    // than on submit() returning, so a submission that fails stays put with its rail intact.
    useEffect(() => {
        if (result?.lensId) {
            goToLensPage(result.lensId);
        }
    }, [goToLensPage, result?.lensId]);

    // One submission body for both entry points (the publish modal's confirm via Formik, and the
    // direct submit-to-folder CTA): explicit visibility/enrollment args rather than reads of
    // Formik state, so the folder CTA cannot race an async setFieldValue.
    const submitLens = (
        name: string,
        submitVisibility: Visibility,
        markedExclusive: boolean,
        creatorPayoutEnrolled: boolean,
    ) => {
        if (!file || !selectedFolder) {
            return;
        }
        sendBlizzardEvent({
            event: "MYLENSES_ACTION",
            actionType: myLensesActionType.submit,
            label: "web_lens_submit",
            namespace: "lenses",
        });
        void submit({
            file,
            logo,
            name: name.trim(),
            visibility: submitVisibility,
            previewMediaItems,
            folderId: selectedFolder.id,
            isMarkedExclusive: markedExclusive,
            lensCreatorPayoutEnrolled: creatorPayoutEnrolled,
            isGameUserProvided,
            buildOutput,
        });
    };

    // Field state lives in Formik (name rules in the schema, mirroring the BFF's); the files and
    // previews stay component state, since they are not form fields. The hook form rather than the
    // render-prop component because the Lens+ interlock below is top-level effects reading and
    // writing form values, which a render prop cannot host.
    const formik = useFormik<WebLensSubmitFormValues>({
        initialValues: { name: "", visibility: Visibility.VisibilityPublic },
        validationSchema: webLensSubmitFormSchema,
        validateOnMount: true,
        onSubmit: (formValues) => {
            submitLens(formValues.name, formValues.visibility, isMarkedExclusive, lensCreatorPayoutEnrolled);
        },
    });
    const { handleSubmit, isValid, setFieldValue, values } = formik;

    // Visibility and Lens Creator Rewards live in the publish modal (mirroring the lens-submission
    // flow's PublishModal), opened by the rail's primary CTA.
    const [publishModalProps, showPublishModal] = useToggleState();

    const availablePublishedVisibilities = visibilityConfig.communityDistributionOptions?.filter(
        (option) => visibilityConfig.disabledOptions?.[option] === undefined,
    );
    const configuredDefaultVisibility = visibilityConfig.defaultVisibility;
    const defaultPublishedVisibility =
        configuredDefaultVisibility && availablePublishedVisibilities?.includes(configuredDefaultVisibility)
            ? configuredDefaultVisibility
            : availablePublishedVisibilities?.[0];
    const canPublishToCommunity = defaultPublishedVisibility !== undefined;
    const canSaveAsDraft =
        visibilityConfig.options.includes(Visibility.VisibilityDontPublishToSnapchat) &&
        visibilityConfig.disabledOptions?.[Visibility.VisibilityDontPublishToSnapchat] === undefined;
    const hasSubmissionPermission = canPublishToCommunity || canSaveAsDraft;
    const submissionPermissionReady =
        !visibilityPermissionsLoading && !visibilityPermissionsError && hasSubmissionPermission;

    const onPublish = useCallback(() => {
        if (!defaultPublishedVisibility) {
            return;
        }
        // Save as Draft uses the same Formik field but is not a modal option. Restore a
        // role-permitted published choice after a failed draft attempt or stale role change.
        if (!availablePublishedVisibilities?.includes(values.visibility)) {
            void setFieldValue("visibility", defaultPublishedVisibility);
        }
        showPublishModal();
    }, [
        availablePublishedVisibilities,
        defaultPublishedVisibility,
        setFieldValue,
        showPublishModal,
        values.visibility,
    ]);

    const onVisibilityChange = useCallback(
        (option: Visibility) => {
            if (!availablePublishedVisibilities?.includes(option)) {
                return;
            }
            void setFieldValue("visibility", option);
        },
        [availablePublishedVisibilities, setFieldValue],
    );

    // Lens Central accepts Lens+ enrollment only on a PUBLIC create. The toggle itself refuses to
    // turn on until its own requirements hold (public visibility, a video preview) — this handler
    // records the choice while keeping the publish visibility explicit.
    const onExclusiveLensChange = useCallback(
        (isExclusive: boolean) => {
            setIsMarkedExclusive(isExclusive);
            if (isExclusive) {
                void setFieldValue("visibility", Visibility.VisibilityPublic);
            }
        },
        [setFieldValue],
    );

    // The modal keeps Lens+ visibility Public, but editable previews can become invalid after selection.
    // Clear enrollment so the creator can fix the preview and opt in again.
    const isExclusiveLensValid = useMemo(
        () => validateExclusiveLens({ previewMediaItems, visibility: values.visibility }).isValid,
        [previewMediaItems, values.visibility],
    );
    useEffect(() => {
        if (isMarkedExclusive && !isExclusiveLensValid) {
            setIsMarkedExclusive(false);
        }
    }, [isMarkedExclusive, isExclusiveLensValid]);

    // One contract, for every row the rail can turn red: if a check would abort submit() in
    // runClientPreChecks, Submit is disabled and the rail says why. A red row next to an enabled
    // Submit button that then fails for exactly that reason is the thing to avoid — so the package
    // is validated here, not merely present, and an attached icon has to be usable (a missing icon
    // is fine; it's optional). The name rules live in the Formik schema (isValid).
    const isPackageReady =
        Boolean(file && isZipPackage(file) && isPackageSizeAccepted(file)) && isBuildOutputWithinBudget(buildOutput);
    const canSubmit =
        isPackageReady &&
        Boolean(selectedFolder) &&
        submissionPermissionReady &&
        isValid &&
        hasVideoPreview &&
        // Belt to the auto-uncheck effect's braces: enrollment must never be submitted while its
        // requirements are unmet, even in the render before the effect lands. The reason shows in
        // the monetization block (the shared warning), not the rail.
        (!isMarkedExclusive || isExclusiveLensValid) &&
        !previewError &&
        (!logo || isLogoAccepted(logo)) &&
        !submitting &&
        !uploadFileLoading;

    // Until submit() runs there is nothing to report on, so the rail shows what is still missing.
    // Once it does, its own rows take over: they describe the submission actually in flight.
    const permissionCheck: SubmissionCheck = {
        id: "lens-folder-permission",
        label: intl.formatMessage(messages.lensFolderPermissionCheckLabel),
        state: visibilityPermissionsLoading ? "pending" : submissionPermissionReady ? "pass" : "fail",
        detail: visibilityPermissionsLoading
            ? undefined
            : visibilityPermissionsError
              ? intl.formatMessage(messages.lensFolderPermissionCheckLoadFail)
              : hasSubmissionPermission
                ? undefined
                : intl.formatMessage(messages.lensFolderPermissionCheckFail),
    };
    const railChecks =
        checks.length > 0
            ? checks
            : [
                  permissionCheck,
                  ...getReadinessChecks(intl, {
                      file,
                      logo,
                      name: values.name,
                      hasVideoPreview,
                      isPreviewTouched: lensPreviewProps.isTouched,
                      buildOutput,
                  }),
              ];

    // Surfaces the in-flight submission's first failure inside the modal (the real PublishModal
    // shows its error the same way); the rail behind keeps the full row-by-row detail.
    const failedCheck = checks.find((check) => check.state === "fail");

    // The upload page is only meaningful with a destination folder. While orgs load, show the
    // gate-style spinner instead of the form — rendering the form first would let the user start
    // filling it in, then yank it away if the selection turns out stale. Once loaded, a
    // missing/foreign folder goes back to this org's chooser; the invalid ?lens-folder= is
    // dropped (the org survives in the path).
    if (folderSelectionLoading) {
        return <PageContentSpinner />;
    }
    if (!selectedFolder) {
        return <Navigate to={generatePath(AbsoluteRoute.WEB_LENSES, { orgId })} replace />;
    }

    return (
        <div className={styles.page}>
            <PageHeader>
                <PageTitle />
            </PageHeader>
            <PageSubHeader>
                <PageSubtitle
                    documentationLink={
                        <TextLink href={WEB_LENS_DOCUMENTATION_URL} target="_blank" rel="noopener noreferrer">
                            <DocumentationLink />
                        </TextLink>
                    }
                />
            </PageSubHeader>

            <FormikProvider value={formik}>
                <div className={styles.columns}>
                    <div className={styles.mainColumn}>
                        <Card>
                            <div className={styles.cardBody}>
                                {/* selectedFolder is non-null past the guard above; the org can only be
                                    null in the impossible folder-without-org case, so this collapses to
                                    one check. */}
                                {selectedOrganization ? (
                                    <div className={styles.destinationRow} data-testid="web-lens-destination">
                                        <span>
                                            <SubmitDestination
                                                organization={
                                                    <span className={styles.destinationName}>
                                                        {selectedOrganization.name}
                                                    </span>
                                                }
                                                folder={
                                                    <span className={styles.destinationName}>
                                                        {selectedFolder.name}
                                                    </span>
                                                }
                                            />
                                        </span>
                                        {/* Back to the chooser with the current selection intact, so it
                                            opens prefilled. */}
                                        <Link
                                            className={clsx(styles.textLink, styles.destinationChange)}
                                            to={{
                                                pathname: generatePath(AbsoluteRoute.WEB_LENSES, { orgId }),
                                                search: location.search,
                                            }}
                                        >
                                            <ChangeDestination />
                                        </Link>
                                    </div>
                                ) : null}

                                <div className={styles.section}>
                                    <CardTitle>
                                        <PackageSectionTitle />
                                    </CardTitle>
                                    <WebLensPackageDropzone
                                        file={file}
                                        buildOutput={buildOutput}
                                        disabled={submitting}
                                        onPick={(picked) => {
                                            setFile(picked);
                                            reset();
                                        }}
                                    />
                                </div>

                                <div className={styles.divider} />

                                <div className={styles.section}>
                                    <CardTitle>
                                        <DetailsSectionTitle />
                                    </CardTitle>
                                    <div className={styles.splitPanel}>
                                        <div className={styles.previewColumn}>
                                            {/* disabled while submitting: the preview editor's CTAs could
                                                otherwise swap previews after submit() captured its refs,
                                                showing a preview the new lens never received. */}
                                            <LensPreview
                                                {...lensPreviewProps}
                                                uploadFileLoading={uploadFileLoading}
                                                surfaceType={SNAPCHAT_SURFACE}
                                                disabled={submitting}
                                                badgeText={visiblePreviewError ? <InvalidPreviewBadge /> : undefined}
                                                badgeStatus={visiblePreviewError ? Status.ERROR : undefined}
                                                videoDurationSpec={
                                                    isGameUserProvided ? (
                                                        <RequirementPreviewGame />
                                                    ) : (
                                                        <RequirementPreviewVideo />
                                                    )
                                                }
                                            />
                                            {/* The badge only says "Invalid", so the reason sits
                                                under the control that fixes it — the same preview
                                                editor, reopened with Change Lens Preview. */}
                                            {visiblePreviewError ? (
                                                <p className={styles.previewError} data-testid="web-lens-preview-error">
                                                    {visiblePreviewError}
                                                </p>
                                            ) : null}
                                        </div>
                                        <div className={styles.detailsColumn}>
                                            <WebLensNameField disabled={submitting} />

                                            <WebLensIconControl
                                                logo={logo}
                                                disabled={submitting}
                                                onPick={(picked) => {
                                                    setLogo(picked);
                                                    reset();
                                                }}
                                                cta={<UploadLogoCta />}
                                                hint={
                                                    <LogoHint
                                                        limit={formatMegabytes(LOGO_MAX_BYTES)}
                                                        dimension={LOGO_MAX_DIMENSION_PX}
                                                    />
                                                }
                                            />

                                            {/* The submission flow's own game panel (criteria modal
                                                included); the flag rides CreateLens.isGameUserProvided
                                                and routes review to the games queue. */}
                                            <WebLensGameLensToggle
                                                isGameUserProvided={isGameUserProvided}
                                                onGameLensToggle={handleGameLensToggle}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>

                    <div className={styles.rail}>
                        <Card>
                            <div className={styles.railBody}>
                                <CardTitle>
                                    <RailTitle />
                                </CardTitle>
                                <SubmissionChecklist checks={railChecks} />
                                <div className={styles.divider} />
                                <LensSubmissionDisclaimer className={styles.railDisclaimer} />
                                {/* The same two CTAs as the submission flow's PublishPanel: the
                                    primary opens the publish modal (rewards, quota);
                                    the secondary submits straight to the folder without
                                    publishing — no modal, rewards off. */}
                                <div className={styles.submitButton}>
                                    <Button
                                        type="primary"
                                        isDisabled={!canSubmit || !canPublishToCommunity}
                                        onClick={onPublish}
                                        data-testid="web-lens-submit"
                                    >
                                        <SubmitCta />
                                    </Button>
                                </div>
                                <div className={styles.submitButton}>
                                    <Button
                                        isDisabled={!canSubmit || !canSaveAsDraft}
                                        onClick={() => {
                                            if (!canSaveAsDraft) {
                                                return;
                                            }
                                            void setFieldValue(
                                                "visibility",
                                                Visibility.VisibilityDontPublishToSnapchat,
                                            );
                                            setIsMarkedExclusive(false);
                                            setLensCreatorPayoutEnrolled(false);
                                            submitLens(
                                                values.name,
                                                Visibility.VisibilityDontPublishToSnapchat,
                                                false,
                                                false,
                                            );
                                        }}
                                        data-testid="web-lens-submit-to-folder"
                                    >
                                        {submitting ? <SubmittingCta /> : <SaveAsDraftCta />}
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>

                <WebLensPublishModal
                    {...publishModalProps}
                    lensName={values.name.trim()}
                    visibilityConfig={visibilityConfig}
                    visibility={values.visibility}
                    onVisibilityChange={onVisibilityChange}
                    isMyLensesCreatorPayoutEnabled={isMyLensesCreatorPayoutEnabled}
                    isCreatorPayoutEnrolled={lensCreatorPayoutEnrolled}
                    onCreatorPayoutEnrolledChange={setLensCreatorPayoutEnrolled}
                    isExclusiveLensChecked={isMarkedExclusive}
                    onExclusiveLensChange={onExclusiveLensChange}
                    previewMediaItems={previewMediaItems}
                    lensPreviewProps={lensPreviewProps}
                    onConfirm={handleSubmit}
                    confirmLabel={submitting ? <SubmittingCta /> : <SubmitCta />}
                    confirmIsPending={submitting}
                    confirmIsDisabled={!canSubmit || !canPublishToCommunity}
                    errorMessage={
                        failedCheck ? failedCheck.detail ?? intl.formatMessage(messages.genericFailure) : null
                    }
                />
            </FormikProvider>
        </div>
    );
};
